/**
 * MobileTextRecruiterFAB — global floating "Text Recruiter" button.
 *
 * Visible on mobile only (lg:hidden). Appears after the user has scrolled past the
 * fold (~280 px) so it doesn't crowd the hero. Subtle dot pulse on the message
 * icon — the entire button does NOT pulse (would be noisy / accessibility issue).
 * Includes a dismiss option that hides the FAB for the rest of the session.
 *
 * Tap opens sms:+18323441525 (Recruiter — Jobs).
 */

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X } from "lucide-react";
import { RECRUITER_SMS_HREF } from "./SMSRecruiterCard";
import { trackEvent } from "../lib/analytics";

const SESSION_KEY = "tc_fab_dismissed_v1";

export default function MobileTextRecruiterFAB() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(SESSION_KEY) === "1") return;
    const onScroll = () => {
      if (window.scrollY > 280) setShow(true);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dismiss = () => {
    setShow(false);
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch {}
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 22 }}
          className="lg:hidden fixed z-40 right-4 bottom-4 print:hidden"
          data-testid="mobile-text-recruiter-fab"
        >
          <div className="flex items-center gap-1.5">
            <a
              href={RECRUITER_SMS_HREF}
              onClick={() => trackEvent("sms_recruiter_click", { surface: "mobile-fab" })}
              className="relative inline-flex items-center gap-2 px-4 py-3 rounded-full bg-trital-navy text-white font-semibold text-[13px] shadow-navy hover:bg-trital-navy-deep focus:outline-none focus:ring-2 focus:ring-white/40"
              data-testid="fab-text-recruiter"
              aria-label="Text our recruiter at (832) 344-1525"
            >
              <span className="relative flex w-2 h-2 shrink-0" aria-hidden="true">
                <span className="absolute inline-flex h-full w-full rounded-full bg-trital-red opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-trital-red" />
              </span>
              <MessageSquare className="w-4 h-4" strokeWidth={1.8} />
              Text Recruiter
            </a>
            <button
              type="button"
              onClick={dismiss}
              className="w-9 h-9 rounded-full bg-white/95 border border-slate-200 text-slate-500 hover:text-trital-red shadow-md flex items-center justify-center"
              aria-label="Hide text-recruiter button for this session"
              data-testid="fab-dismiss"
            >
              <X className="w-4 h-4" strokeWidth={1.8} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
